import { createContext, useContext, useEffect, useState, type ReactNode } from "react"
import { apiFetch, tokenStore, ApiError } from "@/lib/api"

export interface User {
  id: string
  email: string
  name: string
  avatarUrl?: string | null
  totpEnabled: boolean
}

interface AuthState {
  user: User | null
  loading: boolean
  login: (email: string, password: string, totpCode?: string) => Promise<void>
  register: (input: {
    email: string
    password: string
    name: string
    householdName?: string
  }) => Promise<void>
  logout: () => Promise<void>
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  async function refreshUser() {
    if (!tokenStore.getAccess()) {
      setUser(null)
      setLoading(false)
      return
    }
    try {
      const data = await apiFetch<{ user: User }>("/me")
      setUser(data.user)
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        tokenStore.clear()
        setUser(null)
      } else {
        throw err
      }
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    refreshUser()
  }, [])

  async function login(email: string, password: string, totpCode?: string) {
    const data = await apiFetch<{
      user: User
      accessToken: string
      refreshToken: string
    }>("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password, totpCode }),
      skipAuth: true,
    })
    tokenStore.set(data.accessToken, data.refreshToken)
    setUser(data.user)
  }

  async function register(input: {
    email: string
    password: string
    name: string
    householdName?: string
  }) {
    const data = await apiFetch<{
      user: User
      accessToken: string
      refreshToken: string
    }>("/auth/register", {
      method: "POST",
      body: JSON.stringify(input),
      skipAuth: true,
    })
    tokenStore.set(data.accessToken, data.refreshToken)
    setUser(data.user)
  }

  async function logout() {
    const refresh = tokenStore.getRefresh()
    if (refresh) {
      try {
        await apiFetch("/auth/logout", {
          method: "POST",
          body: JSON.stringify({ refreshToken: refresh }),
          skipAuth: true,
        })
      } catch {
        // sempre limpa local mesmo se backend falhar
      }
    }
    tokenStore.clear()
    setUser(null)
  }

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider")
  return ctx
}
